import { getToken } from 'helpers';
import { useAppDispatch, useAppSelector } from 'hooks';
import React, { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shopListAction } from 'slices/shopSlice';
import * as cts from '../../../constants/ui/svg';
import { generateImage, useDebounceFn } from 'helpers/functionHelper';
import ShopNoLogo from '../../../assets/images/no-logo.png';
import { Empty, Spin, Switch } from 'antd';
import { Shop } from 'models';
import { shopSettingActions } from 'slices/shopSettingSlice';
import { LoadingOutlined } from '@ant-design/icons';
const ShowShopMasterTab = forwardRef((_, ref) => {
  // define
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const token = getToken();

  // selectors
  const listShop = useAppSelector((state) => state.shopList.listShop);
  const listShopLoading = useAppSelector((state) => state.shopList.listShopLoading);
  const listPermissionByUser = useAppSelector((state) => state.shopSetting.permissionByUser);
  const permissionByUserLoading = useAppSelector(
    (state) => state.shopSetting.permissionByUserLoading
  );
  const masterInfo = useAppSelector((state) => state.auth.masterInfo);
  const selectedShop = useAppSelector((state) => state.shopList.selectShop);

  // states
  const [searchKey, setSearchKey] = useState('');
  const [shopIsCheck, setShopIsCheck] = useState<Array<Shop & { status: number; flag: number }>>(
    []
  );

  // variable
  const middleArrayOfShop = Math.floor(shopIsCheck.length / 2);

  // funcs
  const handleFindShopIsEnablePermissionByMaster = (item: Shop) => {
    return listPermissionByUser.find(
      (permissionUserItem) =>
        permissionUserItem.shop_id === item.id &&
        permissionUserItem.app_name.includes('11') &&
        permissionUserItem.name === 'enable_ranking_program_master'
    );
  };
  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchKey(value);
  };
  const handleDebounceSearchKeyChange = useDebounceFn(handleSearchChange, 500);
  const onSwitchChange = (itemCheck: Shop, checked: boolean) => {
    const checkStatus = checked ? 1 : 0;
    const indexToUpdate = shopIsCheck.findIndex((item) => {
      return item.name == itemCheck.name;
    });
    if (indexToUpdate !== -1) {
      setShopIsCheck((prev) => {
        const currentShopList = [...prev];
        currentShopList[indexToUpdate].status = checkStatus;
        currentShopList[indexToUpdate].flag = 1;
        return currentShopList;
      });
    }
  };

  const onSave = () => {
    const dataBody = {
      permission: shopIsCheck
        .filter((filterItem) => filterItem.flag)
        .map((item) => {
          const existedEditPermission = listPermissionByUser.find(
            (permissionUserItem) =>
              permissionUserItem.shop_id == item.id &&
              permissionUserItem.name == 'enable_ranking_program_master'
          );
          const baseDataBody = {
            name: 'enable_ranking_program_master',
            active: item.status,
            app_name: [11],
            shop_id: item.id,
          };
          return existedEditPermission
            ? { ...baseDataBody, id: existedEditPermission.id }
            : baseDataBody;
        }),
    };
    if (token) {
      dispatch(shopSettingActions.updatePermissionStaff({ token, dataBody }));
    }
  };
  // handle parent component save button click
  useImperativeHandle(ref, () => ({
    onSave,
  }));

  // useEffects
  // watch search key change
  useEffect(() => {
    if (searchKey !== '') {
      const searchKeyLowerCase = searchKey.toLocaleLowerCase();
      const listShopForSearching = listShop
        .map((item) => {
          return {
            ...item,
            status: handleFindShopIsEnablePermissionByMaster(item)?.active ?? 0,
            flag: 0,
          };
        })
        .filter((item) => item.group_shop == 1);
      const resultAfterSearchFilter = listShopForSearching
        .filter((item: Shop) => {
          return (
            searchKeyLowerCase.includes(item?.name.toLowerCase()) ||
            item?.name.toLowerCase().includes(searchKeyLowerCase)
          );
        })
        .sort((a, b) => a.name.localeCompare(b.name));
      setShopIsCheck(resultAfterSearchFilter);
    } else {
      setShopIsCheck(
        listShop
          .map((item) => {
            return {
              ...item,
              status: handleFindShopIsEnablePermissionByMaster(item)?.active ?? 0,
              flag: 0,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name))
          // filter shop in group shop at 9/3/2026
          .filter((item) => item.group_shop == 1)
      );
    }
  }, [searchKey, listShop.length, listPermissionByUser.length]);

  // set listShop at first time mounted
  // useEffect(() => {
  //   if (listShop.length && listPermissionByUser.length) {
  //     const sortedShopList = listShop
  //       .map((item) => {
  //         const checkShopIsEnable = () => {
  //           return listPermissionByUser.find(
  //             (permissionUserItem) =>
  //               permissionUserItem.shop_id === item.id &&
  //               permissionUserItem.name === 'enable_ranking_program_master'
  //           );
  //         };
  //         return {
  //           ...item,
  //           status: checkShopIsEnable()?.active ?? 0,
  //           flag: 0,
  //         };
  //       })
  //       .sort((a, b) => a.name.localeCompare(b.name));
  //     setShopIsCheck(sortedShopList);
  //   }
  // }, [listShop.length, listPermissionByUser]);

  // fetch list shop
  useEffect(() => {
    if (token) {
      dispatch(shopListAction.fetchShopList({ token }));
      dispatch(shopSettingActions.getPermissionByUser({ token }));
    }
  }, [dispatch, token]);

  if (!masterInfo && selectedShop.id) {
    return <></>;
  }

  return (
    <Spin
      spinning={listShopLoading || permissionByUserLoading}
      indicator={<LoadingOutlined style={{ fontSize: 48, color: '#666687' }} spin />}
    >
      <div className="px-4 py-3">
        <div className="flex flex-col gap-2 justify-between items-center lg:flex-row lg:gap-0">
          <span className="self-center text-xl lg:self-start font-notosans-700 text-gray_800">
            {t('SHOP_LIST')}
          </span>
          <div className="flex gap-2 items-center h-10 bg-white rounded-lg border border-solid search-bar border-border-grays">
            <button className="pl-2 !transition-none">{cts.searchIcon}</button>
            <input
              placeholder="店舗を検索する"
              type="text"
              className="h-full rounded-l-lg focus:outline-none min-w-[120px] w-full font-notosans-500"
              onChange={(e) => handleDebounceSearchKeyChange(e)}
            />
            <button className="px-4 h-full text-white whitespace-nowrap rounded-r-lg border-l border-solid border-primary_border bg-primary notosans500">
              {t('SEARCH')}
            </button>
          </div>
        </div>
        {/* PC */}
        {shopIsCheck.length !== 0 ? (
          <div className="lg:flex hidden items-center h-[calc(100vh-240px)] mt-4">
            <div className="overflow-auto w-1/2 h-full">
              {(searchKey || shopIsCheck.length === 1
                ? shopIsCheck
                : shopIsCheck.slice(0, middleArrayOfShop)
              ).map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between items-center py-5 pr-2 border-b last:border-b-0 border-border-gray"
                >
                  <div className="flex gap-2 items-center">
                    <img
                      className="w-[60px] h-10 object-cover rounded-md"
                      src={
                        item.avatar_image && (item?.avatar_image ?? '')
                          ? generateImage(item?.avatar_image)
                          : ShopNoLogo
                      }
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = ShopNoLogo;
                      }}
                      alt="shop-avatar"
                    />
                    <p className="text-base font-extrabold font-notosans-700 text-text-primary">
                      {item.name}
                    </p>
                  </div>

                  <Switch
                    checked={Boolean(item.status)}
                    className="switch_rank"
                    onChange={(e) => onSwitchChange(item, e)}
                  />
                </div>
              ))}
            </div>
            <div className="mr-6 ml-4 w-px h-full bg-border-gray"></div>
            <div className="overflow-auto w-1/2 h-full">
              {shopIsCheck.slice(middleArrayOfShop, shopIsCheck.length - 1).map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between items-center py-5 pr-2 border-b last:border-b-0 border-border-gray"
                >
                  <div className="flex gap-2 items-center">
                    <img
                      className="w-[60px] h-10 object-cover rounded-md"
                      src={
                        item.avatar_image && (item?.avatar_image ?? '')
                          ? generateImage(item?.avatar_image)
                          : ShopNoLogo
                      }
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = ShopNoLogo;
                      }}
                      alt="shop-avatar"
                    />
                    <p className="text-base font-extrabold font-notosans-700 text-text-primary">
                      {item.name}
                    </p>
                  </div>

                  <Switch
                    checked={Boolean(item.status)}
                    className="switch_rank"
                    onChange={(e) => onSwitchChange(item, e)}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="h-[300px] w-full flex items-center justify-center">
            <Empty description={t('NO_DATA')} />
          </div>
        )}
        {/* Mobile */}
        <div className="lg:hidden h-[calc(100vh-240px)] mt-4">
          {shopIsCheck.length !== 0 ? (
            <div className="overflow-auto h-full">
              {shopIsCheck.map((item) => (
                <div className="flex justify-between items-center py-5 pr-2 border-b border-border-gray">
                  <div className="flex gap-2 items-center">
                    <img
                      className="w-[60px] h-10 object-cover rounded-md"
                      src={
                        item.avatar_image && (item?.avatar_image ?? '')
                          ? generateImage(item?.avatar_image)
                          : ShopNoLogo
                      }
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = ShopNoLogo;
                      }}
                      alt="shop-avatar"
                    />
                    <p className="text-base font-notosans-700 text-text-primary">{item.name}</p>
                  </div>
                  <Switch
                    checked={Boolean(item.status)}
                    className="switch_rank"
                    onChange={(e) => onSwitchChange(item, e)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="h-[300px] w-full flex items-center justify-center">
              <Empty description={t('NO_DATA')} />
            </div>
          )}
        </div>
      </div>
    </Spin>
  );
});

export default ShowShopMasterTab;
